import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  HeadingLevel,
  PageBreak,
  PageNumber,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from 'docx';
import { Assessment, Essay, ModelResult } from '../src/types';

// Brand + neutral palette (hex, no leading #)
const ACCENT = '7C3AED';
const ACCENT_LIGHT = 'EDE7FB';
const HEADING_TEXT = '1F2937';
const MUTED_TEXT = '6B7280';
const TABLE_BORDER = 'D1D5DB';
const AWARDED_SHADE = 'E9F7EF';
const AWARDED_TEXT = '1E7A3C';

const FONT = 'Calibri';

interface CriterionMarkingResult {
  criterionName?: string;
  selectedBand?: string;
  score?: number;
  justification?: string;
  justificationBandAbove?: string;
  justificationBandBelow?: string;
}

// The subset of marking data the report needs, sourced either from a
// modelResults entry or from the legacy top-level essay fields.
interface ReportMarkingData {
  finalOverallScore: number | null;
  finalOverallBand: string | null;
  feedback: string | null;
  consolidatedMarkingResults: CriterionMarkingResult[];
}

export const getReportMarkingData = (
  essayData: Essay,
  modelId?: string
): ReportMarkingData | null => {
  const modelResults = essayData.modelResults || {};
  const modelIds = Object.keys(modelResults);
  let source: ModelResult | Essay | undefined;

  if (modelId && modelResults[modelId]) {
    source = modelResults[modelId];
  } else if (modelIds.length > 0) {
    source = modelResults[modelIds[0]];
  } else if (essayData.feedback || essayData.consolidatedMarkingResults?.length) {
    // Legacy essays marked before multi-model storage existed.
    source = essayData;
  }

  if (!source) return null;

  return {
    finalOverallScore: source.finalOverallScore ?? null,
    finalOverallBand: source.finalOverallBand ?? null,
    feedback: source.feedback ?? null,
    consolidatedMarkingResults: (source.consolidatedMarkingResults || []) as CriterionMarkingResult[],
  };
};

export const hasExportableResult = (essayData: Essay): boolean =>
  getReportMarkingData(essayData) !== null;

const sanitizeFileNamePart = (part: string): string =>
  part.replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim();

export const buildReportFileName = (essayData: Essay, assessment?: Assessment | null): string => {
  const student = sanitizeFileNamePart(essayData.studentName || 'Student');
  const title = sanitizeFileNamePart(assessment?.name || assessment?.title || '');
  return title ? `${student} - ${title} - Feedback.docx` : `${student} - Feedback.docx`;
};

// --- Paragraph helpers -------------------------------------------------------

const normalText = (text: string, opts: { bold?: boolean; italics?: boolean; color?: string; size?: number } = {}) =>
  new TextRun({ text, font: FONT, size: opts.size ?? 22, bold: opts.bold, italics: opts.italics, color: opts.color });

// Splits "a **b** c" into styled runs.
const runsFromMarkdownInline = (text: string, base: { italics?: boolean; color?: string } = {}): TextRun[] =>
  text
    .split(/(\*\*.*?\*\*)/g)
    .filter(Boolean)
    .map(part =>
      part.startsWith('**') && part.endsWith('**')
        ? normalText(part.slice(2, -2), { bold: true, ...base })
        : normalText(part, base)
    );

const sectionHeading = (text: string): Paragraph =>
  new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 360, after: 160 },
    border: { bottom: { color: ACCENT, space: 2, style: BorderStyle.SINGLE, size: 8 } },
    children: [new TextRun({ text, font: FONT, size: 30, bold: true, color: HEADING_TEXT })],
  });

const subHeading = (text: string): Paragraph =>
  new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 240, after: 100 },
    children: [new TextRun({ text, font: FONT, size: 25, bold: true, color: ACCENT })],
  });

const bodyParagraph = (text: string): Paragraph =>
  new Paragraph({ spacing: { after: 120 }, children: runsFromMarkdownInline(text) });

const labelledParagraph = (label: string, text: string): Paragraph =>
  new Paragraph({
    spacing: { after: 120 },
    children: [normalText(`${label} `, { bold: true }), ...runsFromMarkdownInline(text)],
  });

const spacer = (): Paragraph => new Paragraph({ spacing: { after: 60 }, children: [] });

// Markdown feedback (headings/bullets/bold/blockquotes) → Word paragraphs.
const markdownToParagraphs = (markdown: string): Paragraph[] => {
  const paragraphs: Paragraph[] = [];
  markdown.split('\n').forEach(line => {
    if (line.startsWith('### ') || line.startsWith('## ')) {
      paragraphs.push(subHeading(line.replace(/^#+\s+/, '')));
      return;
    }
    if (line.startsWith('> ')) {
      paragraphs.push(
        new Paragraph({
          indent: { left: 480 },
          spacing: { after: 120 },
          border: { left: { color: ACCENT, space: 4, style: BorderStyle.SINGLE, size: 12 } },
          children: runsFromMarkdownInline(line.substring(2), { italics: true, color: MUTED_TEXT }),
        })
      );
      return;
    }
    const bulletMatch = line.match(/^(\s*)[*-]\s+(.*)/);
    if (bulletMatch) {
      paragraphs.push(
        new Paragraph({
          bullet: { level: bulletMatch[1].length >= 2 ? 1 : 0 },
          spacing: { after: 80 },
          children: runsFromMarkdownInline(bulletMatch[2]),
        })
      );
      return;
    }
    if (line.trim().length > 0) {
      paragraphs.push(bodyParagraph(line));
    }
  });
  return paragraphs;
};

// --- Table helpers -----------------------------------------------------------

const cellBorders = {
  top: { style: BorderStyle.SINGLE, size: 4, color: TABLE_BORDER },
  bottom: { style: BorderStyle.SINGLE, size: 4, color: TABLE_BORDER },
  left: { style: BorderStyle.SINGLE, size: 4, color: TABLE_BORDER },
  right: { style: BorderStyle.SINGLE, size: 4, color: TABLE_BORDER },
};

const headerCell = (text: string, widthPct: number): TableCell =>
  new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.CLEAR, fill: ACCENT },
    verticalAlign: VerticalAlign.CENTER,
    borders: cellBorders,
    margins: { top: 100, bottom: 100, left: 140, right: 140 },
    children: [
      new Paragraph({
        children: [new TextRun({ text, font: FONT, size: 21, bold: true, color: 'FFFFFF' })],
      }),
    ],
  });

const bodyCell = (
  text: string,
  widthPct: number,
  opts: { bold?: boolean; fill?: string; color?: string; align?: (typeof AlignmentType)[keyof typeof AlignmentType] } = {}
): TableCell =>
  new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: opts.fill ? { type: ShadingType.CLEAR, fill: opts.fill } : undefined,
    verticalAlign: VerticalAlign.CENTER,
    borders: cellBorders,
    margins: { top: 80, bottom: 80, left: 140, right: 140 },
    children: [
      new Paragraph({
        alignment: opts.align,
        children: [normalText(text, { bold: opts.bold, color: opts.color, size: 21 })],
      }),
    ],
  });

// --- Section builders --------------------------------------------------------

const buildTitleBlock = (essayData: Essay, assessment: Assessment | null | undefined): Paragraph[] => {
  const out: Paragraph[] = [];
  const title = assessment?.name || assessment?.title || 'Essay Assessment';

  out.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [new TextRun({ text: 'Essay Feedback Report', font: FONT, size: 20, bold: true, color: ACCENT, allCaps: true })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
      children: [new TextRun({ text: title, font: FONT, size: 40, bold: true, color: HEADING_TEXT })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 80 },
      border: { bottom: { color: ACCENT, space: 8, style: BorderStyle.SINGLE, size: 12 } },
      children: [new TextRun({ text: essayData.studentName || 'Student', font: FONT, size: 28, color: HEADING_TEXT })],
    })
  );

  const detailBits: string[] = [];
  if (assessment?.subject) detailBits.push(assessment.subject);
  if (assessment?.yearGroup) detailBits.push(`Year ${assessment.yearGroup}`.replace(/^Year Year/i, 'Year'));
  detailBits.push(
    new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
  );
  if (typeof essayData.wordCount === 'number') detailBits.push(`${essayData.wordCount} words`);

  out.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 },
      children: [new TextRun({ text: detailBits.join('   •   '), font: FONT, size: 20, color: MUTED_TEXT })],
    })
  );

  return out;
};

const buildResultSummary = (
  essayData: Essay,
  assessment: Assessment | null | undefined,
  marking: ReportMarkingData
): (Paragraph | Table)[] => {
  const total = assessment?.totalMarks;
  const scoreText =
    marking.finalOverallScore !== null
      ? `${marking.finalOverallScore}${typeof total === 'number' ? ` / ${total}` : ''}`
      : 'N/A';

  const rows: TableRow[] = [
    new TableRow({ children: [headerCell('Overall Result', 60), headerCell('', 40)] }),
    new TableRow({
      children: [
        bodyCell('Overall mark', 60, { bold: true }),
        bodyCell(scoreText, 40, { bold: true, align: AlignmentType.CENTER }),
      ],
    }),
  ];
  if (marking.finalOverallBand) {
    rows.push(
      new TableRow({
        children: [bodyCell('Overall band', 60), bodyCell(marking.finalOverallBand, 40, { align: AlignmentType.CENTER })],
      })
    );
  }
  if (typeof essayData.manualOverallScore === 'number') {
    rows.push(
      new TableRow({
        children: [
          bodyCell("Teacher's confirmed mark", 60),
          bodyCell(
            `${essayData.manualOverallScore}${typeof total === 'number' ? ` / ${total}` : ''}`,
            40,
            { bold: true, fill: AWARDED_SHADE, color: AWARDED_TEXT, align: AlignmentType.CENTER }
          ),
        ],
      })
    );
  }

  return [new Table({ width: { size: 60, type: WidthType.PERCENTAGE }, alignment: AlignmentType.CENTER, rows }), spacer()];
};

const buildFeedbackSection = (essayData: Essay, marking: ReportMarkingData): Paragraph[] => {
  const out: Paragraph[] = [sectionHeading('Feedback')];

  if (essayData.teacherFeedback?.trim()) {
    out.push(subHeading('Comments from your teacher'));
    out.push(...markdownToParagraphs(essayData.teacherFeedback));
  }

  if (marking.feedback?.trim()) {
    if (essayData.teacherFeedback?.trim()) {
      out.push(subHeading('Detailed feedback'));
    }
    out.push(...markdownToParagraphs(marking.feedback));
  } else if (!essayData.teacherFeedback?.trim()) {
    out.push(bodyParagraph('No written feedback was generated for this essay.'));
  }

  return out;
};

const buildCriteriaSection = (
  assessment: Assessment | null | undefined,
  marking: ReportMarkingData
): (Paragraph | Table)[] => {
  const results = marking.consolidatedMarkingResults;
  if (!results.length) return [];

  const out: (Paragraph | Table)[] = [sectionHeading('Marks by Criterion')];

  const findWeight = (criterionName?: string): number | undefined =>
    assessment?.criteria?.find(
      c => c.name?.toLowerCase().trim() === criterionName?.toLowerCase().trim()
    )?.weight;

  // Summary table
  const rows: TableRow[] = [
    new TableRow({
      children: [headerCell('Criterion', 46), headerCell('Weighting', 18), headerCell('Band awarded', 18), headerCell('Score', 18)],
    }),
  ];
  results.forEach(crit => {
    const weight = findWeight(crit.criterionName);
    rows.push(
      new TableRow({
        children: [
          bodyCell(crit.criterionName || 'Criterion', 46),
          bodyCell(typeof weight === 'number' ? `${weight}%` : '—', 18, { align: AlignmentType.CENTER }),
          bodyCell(crit.selectedBand || '—', 18, { align: AlignmentType.CENTER }),
          bodyCell(crit.score !== undefined && crit.score !== null ? String(crit.score) : '—', 18, {
            bold: true,
            align: AlignmentType.CENTER,
          }),
        ],
      })
    );
  });
  out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows }), spacer());

  // Per-criterion explanations
  results.forEach(crit => {
    out.push(subHeading(crit.criterionName || 'Criterion'));
    const bandLine =
      crit.selectedBand && crit.score !== undefined && crit.score !== null
        ? `Band awarded: ${crit.selectedBand}   •   Score: ${crit.score}`
        : crit.selectedBand
          ? `Band awarded: ${crit.selectedBand}`
          : null;
    if (bandLine) {
      out.push(
        new Paragraph({
          spacing: { after: 100 },
          shading: { type: ShadingType.CLEAR, fill: ACCENT_LIGHT },
          children: [normalText(bandLine, { bold: true, color: HEADING_TEXT })],
        })
      );
    }
    if (crit.justification) {
      out.push(labelledParagraph('Why this band was awarded:', crit.justification));
    }
    if (crit.justificationBandAbove) {
      out.push(labelledParagraph('To reach the next band:', crit.justificationBandAbove));
    }
    if (crit.justificationBandBelow) {
      out.push(labelledParagraph('Why the band below was not selected:', crit.justificationBandBelow));
    }
  });

  return out;
};

// Compares band labels like "21-25" while tolerating dash/spacing variants.
const bandMatchesLabel = (min: number, max: number, selectedBand?: string): boolean => {
  if (!selectedBand) return false;
  const match = selectedBand.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (match) return parseInt(match[1], 10) === min && parseInt(match[2], 10) === max;
  return selectedBand.trim() === `${min}-${max}`;
};

const buildAppendices = (
  essayData: Essay,
  assessment: Assessment | null | undefined,
  marking: ReportMarkingData
): (Paragraph | Table)[] => {
  const out: (Paragraph | Table)[] = [];
  let appendixIndex = 0;
  const appendixLetter = () => String.fromCharCode(65 + appendixIndex++);

  const pageBreak = () => new Paragraph({ children: [new PageBreak()] });

  if (assessment?.question) {
    out.push(pageBreak(), sectionHeading(`Appendix ${appendixLetter()} — Assessment Question`));
    assessment.question.split('\n').forEach(line => {
      if (line.trim()) out.push(bodyParagraph(line));
    });
  }

  if (assessment?.criteria?.length) {
    out.push(pageBreak(), sectionHeading(`Appendix ${appendixLetter()} — Marking Criteria`));
    if (typeof assessment.totalMarks === 'number') {
      out.push(labelledParagraph('Total marks:', String(assessment.totalMarks)));
    }
    assessment.criteria.forEach(criterion => {
      const awarded = marking.consolidatedMarkingResults.find(
        r => r.criterionName?.toLowerCase().trim() === criterion.name?.toLowerCase().trim()
      );
      out.push(
        subHeading(
          typeof criterion.weight === 'number' ? `${criterion.name} (${criterion.weight}%)` : criterion.name
        )
      );
      const bands = [...(criterion.bands || [])].sort((a, b) => (b.max || 0) - (a.max || 0));
      if (!bands.length) return;
      const rows: TableRow[] = [
        new TableRow({ children: [headerCell('Marks', 16), headerCell('Descriptor', 70), headerCell('', 14)] }),
      ];
      bands.forEach(band => {
        const isAwarded = bandMatchesLabel(band.min, band.max, awarded?.selectedBand);
        const fill = isAwarded ? AWARDED_SHADE : undefined;
        rows.push(
          new TableRow({
            children: [
              bodyCell(`${band.min}–${band.max}`, 16, { bold: isAwarded, fill, align: AlignmentType.CENTER }),
              bodyCell(band.descriptor || '', 70, { fill }),
              bodyCell(isAwarded ? '✔ Awarded' : '', 14, {
                bold: true,
                fill,
                color: isAwarded ? AWARDED_TEXT : undefined,
                align: AlignmentType.CENTER,
              }),
            ],
          })
        );
      });
      out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows }), spacer());
    });
  } else if (assessment?.rubric) {
    out.push(pageBreak(), sectionHeading(`Appendix ${appendixLetter()} — Marking Criteria`));
    assessment.rubric.split('\n').forEach(line => {
      if (line.trim()) out.push(bodyParagraph(line));
    });
  }

  if (essayData.transcribedText?.trim()) {
    out.push(pageBreak(), sectionHeading(`Appendix ${appendixLetter()} — Your Essay (Transcribed)`));
    out.push(
      new Paragraph({
        spacing: { after: 160 },
        children: [
          normalText(
            'This is the text that was transcribed from the submitted essay and used for marking.',
            { italics: true, color: MUTED_TEXT }
          ),
        ],
      })
    );
    essayData.transcribedText.split('\n').forEach(line => {
      out.push(
        line.trim()
          ? new Paragraph({ spacing: { after: 100 }, children: [normalText(line)] })
          : new Paragraph({ children: [] })
      );
    });
  }

  return out;
};

// --- Entry point -------------------------------------------------------------

export const generateStudentReportDocx = async (
  essayData: Essay,
  assessment: Assessment | null | undefined,
  modelId?: string
): Promise<Blob> => {
  const marking = getReportMarkingData(essayData, modelId);
  if (!marking) {
    throw new Error('No marking results are available to export for this essay.');
  }

  const children: (Paragraph | Table)[] = [
    ...buildTitleBlock(essayData, assessment),
    ...buildResultSummary(essayData, assessment, marking),
    ...buildFeedbackSection(essayData, marking),
    ...buildCriteriaSection(assessment, marking),
    ...buildAppendices(essayData, assessment, marking),
  ];

  const doc = new Document({
    creator: 'AI Marker Mate',
    title: `Essay Feedback — ${essayData.studentName || 'Student'}`,
    styles: {
      default: {
        document: { run: { font: FONT, size: 22, color: '374151' } },
      },
    },
    sections: [
      {
        properties: {
          page: { margin: { top: 1080, bottom: 1080, left: 1260, right: 1260 } },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                border: { top: { color: TABLE_BORDER, space: 4, style: BorderStyle.SINGLE, size: 4 } },
                children: [
                  new TextRun({ text: `${essayData.studentName || 'Student'}  •  Page `, font: FONT, size: 16, color: MUTED_TEXT }),
                  new TextRun({ children: [PageNumber.CURRENT], font: FONT, size: 16, color: MUTED_TEXT }),
                  new TextRun({ text: ' of ', font: FONT, size: 16, color: MUTED_TEXT }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], font: FONT, size: 16, color: MUTED_TEXT }),
                  new TextRun({ text: '  •  Generated by AI Marker Mate', font: FONT, size: 16, color: MUTED_TEXT }),
                ],
              }),
            ],
          }),
        },
        children,
      },
    ],
  });

  return Packer.toBlob(doc);
};
